import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useMemo, useState } from 'react';
import { hasPermission } from '@/types';
import {
  trips as api, brigades as brigadesApi, employees as employeesApi,
  vehicles as vehiclesApi, intakes as intakesApi,
} from '@/api/endpoints';
import type {
  Trip, TripInput, TripMeta, TripPurpose, TripStatus, TripRole,
  Brigade, Employee, Vehicle, Intake,
} from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { MonthCalendar, type CalendarEvent } from '@/components/MonthCalendar';
import { ModalShell } from './KadryDetailPage';
import { useAuth } from '@/auth/AuthContext';

/** Data lokalna → 'YYYY-MM-DD'. */
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function KalendarzPage() {
  const { user } = useAuth();
  // Dwie różne bramki po stronie API: przejazdy to TRIP_MANAGE (TripApi), brygady
  // CALENDAR_MANAGE (BrigadeApi). Sklejenie ich odebrałoby geologowi planowanie przejazdów.
  const canPlan = hasPermission(user, 'TRIP_MANAGE');
  const canManageBrigades = hasPermission(user, 'CALENDAR_MANAGE');

  const [month, setMonth] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [view, setView] = useState<'month' | 'list'>('month');
  const [tripsList, setTripsList] = useState<Trip[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [modal, setModal] = useState<Trip | { prefillDate: string } | null>(null);
  const [brigadesOpen, setBrigadesOpen] = useState(false);

  // Okno siatki: 42 komórki od poniedziałku tygodnia z 1. dniem miesiąca.
  const range = useMemo(() => {
    const y = month.getFullYear(), m = month.getMonth();
    const startWeekday = (new Date(y, m, 1).getDay() + 6) % 7;
    return { from: ymd(new Date(y, m, 1 - startWeekday)), to: ymd(new Date(y, m, 1 - startWeekday + 41)) };
  }, [month]);

  const reload = () => {
    api.range(range.from, range.to).then(setTripsList).catch(e => setErr(e?.message ?? String(e)));
  };
  useEffect(reload, [range.from, range.to]);

  const sorted = useMemo(() =>
    [...tripsList].sort((a, b) => a.startDate.localeCompare(b.startDate) || a.title.localeCompare(b.title)),
    [tripsList]);

  return (
    <div className="max-w-[1200px]">
      <header className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1>Kalendarz brygad</h1>
          <div className="text-ink-400 text-[13px]">Planowane wyjazdy brygad — skład, pojazdy, lokalizacja</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <button onClick={() => setView('month')} className={'btn btn-sm ' + (view === 'month' ? 'btn-primary' : 'btn-ghost')}>Miesiąc</button>
            <button onClick={() => setView('list')} className={'btn btn-sm ' + (view === 'list' ? 'btn-primary' : 'btn-ghost')}>Lista</button>
          </div>
          {canManageBrigades && (
            <button onClick={() => setBrigadesOpen(true)} className="btn btn-secondary btn-sm">
              <Icon name="users" className="w-3.5 h-3.5"/> Brygady
            </button>
          )}
          {canPlan && (
            <button onClick={() => setModal({ prefillDate: ymd(new Date()) })} className="btn btn-primary btn-sm">
              <Icon name="plus" className="w-3.5 h-3.5"/> Nowy wyjazd
            </button>
          )}
        </div>
      </header>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}

      {view === 'month' ? (
        <MonthCalendar
          month={month}
          events={tripsList.map((t): CalendarEvent => ({
            id: t.id, title: t.title, startDate: t.startDate, endDate: t.endDate,
            color: t.color || t.purposeColor, startTime: t.startTime,
            tooltip: `${t.title}${t.locationText ? ' · ' + t.locationText : ''}${t.brigadeName ? ' · ' + t.brigadeName : ''}`,
          }))}
          onPrevMonth={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
          onNextMonth={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
          onToday={() => { const n = new Date(); setMonth(new Date(n.getFullYear(), n.getMonth(), 1)); }}
          onDayClick={iso => { if (canPlan) setModal({ prefillDate: iso }); }}
          onEventClick={ev => { const t = tripsList.find(x => x.id === ev.id); if (t) setModal(t); }}
        />
      ) : (
        <div className="card"><div className="card-body !p-0">
          {sorted.length === 0 ? <div className="p-6 text-center text-ink-400 text-[12px]">Brak wyjazdów w tym okresie.</div> : (
            <table className="table-hw">
              <thead><tr><th className="w-40">Termin</th><th>Wyjazd</th><th className="w-40">Brygada / skład</th><th className="w-28">Status</th></tr></thead>
              <tbody>
                {sorted.map(t => (
                  <tr key={t.id} className="cursor-pointer hover:bg-ink-50/50" onClick={() => setModal(t)}>
                    <td className="font-mono text-[12px]">
                      {t.startDate}{t.endDate !== t.startDate ? ' – ' + t.endDate : ''}
                      {t.startTime && <div className="text-[10px] text-ink-400">{t.startTime.slice(0, 5)}{t.endTime ? '–' + t.endTime.slice(0, 5) : ''}</div>}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ background: t.color || t.purposeColor }}/>
                        <div>
                          <div className="font-semibold text-ink-900">{t.title}</div>
                          <div className="text-[11px] text-ink-400">{t.purposeName}{t.locationText ? ' · ' + t.locationText : (t.intakeName ? ' · ' + t.intakeName : '')}</div>
                        </div>
                      </div>
                    </td>
                    <td className="text-[12px] text-ink-500">
                      {t.brigadeName && <div className="font-semibold" style={{ color: t.brigadeColor || undefined }}>{t.brigadeName}</div>}
                      {t.assignments.length > 0 ? t.assignments.map(a => a.employeeName).join(', ') : '—'}
                    </td>
                    <td><span className="pill" style={{ background: '#eef2ff' }}>{t.statusName}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div></div>
      )}

      {modal && (
        <TripModal
          trip={'id' in modal ? modal : null}
          prefillDate={'prefillDate' in modal ? modal.prefillDate : undefined}
          readOnly={!canPlan}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); reload(); }}
        />
      )}
      {brigadesOpen && <BrigadesModal onClose={() => { setBrigadesOpen(false); reload(); }}/>}
    </div>
  );
}

function TripModal({ trip, prefillDate, readOnly, onClose, onSaved }: {
  trip: Trip | null;
  prefillDate?: string;
  readOnly: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!trip;
  const [title, setTitle] = useState(trip?.title ?? '');
  const [purpose, setPurpose] = useState<TripPurpose>(trip?.purpose ?? 'DRILLING');
  const [status, setStatus] = useState<TripStatus>(trip?.status ?? 'PLANNED');
  const [startDate, setStartDate] = useState(trip?.startDate ?? prefillDate ?? ymd(new Date()));
  const [endDate, setEndDate] = useState(trip?.endDate ?? prefillDate ?? ymd(new Date()));
  const [startTime, setStartTime] = useState(trip?.startTime?.slice(0, 5) ?? '');
  const [endTime, setEndTime] = useState(trip?.endTime?.slice(0, 5) ?? '');
  const [locationText, setLocationText] = useState(trip?.locationText ?? '');
  const [intakeId, setIntakeId] = useState<number | ''>(trip?.intakeId ?? '');
  const [brigadeId, setBrigadeId] = useState<number | ''>(trip?.brigadeId ?? '');
  const [notes, setNotes] = useState(trip?.notes ?? '');
  const [assignments, setAssignments] = useState<{ employeeId: number; role: TripRole }[]>(
    trip?.assignments.filter(a => a.employeeId != null).map(a => ({ employeeId: a.employeeId!, role: a.role })) ?? []);
  const [vehicleIds, setVehicleIds] = useState<number[]>(
    trip?.vehicles.filter(v => v.vehicleId != null).map(v => v.vehicleId!) ?? []);

  const [meta, setMeta] = useState<TripMeta | null>(null);
  const [brigadesList, setBrigadesList] = useState<Brigade[]>([]);
  const [people, setPeople] = useState<Employee[]>([]);
  const [vehiclesList, setVehiclesList] = useState<Vehicle[]>([]);
  const [intakesList, setIntakesList] = useState<Intake[]>([]);
  const [addEmpId, setAddEmpId] = useState<number | ''>('');
  const [addRole, setAddRole] = useState<TripRole>('DRIVER');
  const [addVehId, setAddVehId] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.meta().then(setMeta).catch(() => {});
    brigadesApi.list(true).then(setBrigadesList).catch(() => {});
    employeesApi.list('ACTIVE').then(setPeople).catch(() => {});
    vehiclesApi.list().then(setVehiclesList).catch(() => {});
    intakesApi.list().then(setIntakesList).catch(() => {});
  }, []);

  const empName = (id: number) => people.find(p => p.id === id)?.fullName ?? ('#' + id);
  const vehLabel = (id: number) => { const v = vehiclesList.find(x => x.id === id); return v ? v.label + (v.registrationNumber ? ` (${v.registrationNumber})` : '') : '#' + id; };
  const roleName = (r: TripRole) => meta?.roles.find(x => x.id === r)?.name ?? r;

  const addAssignment = () => {
    if (addEmpId === '' || assignments.some(a => a.employeeId === addEmpId)) return;
    setAssignments([...assignments, { employeeId: Number(addEmpId), role: addRole }]);
    setAddEmpId('');
  };
  const addVehicle = () => {
    if (addVehId === '' || vehicleIds.includes(Number(addVehId))) return;
    setVehicleIds([...vehicleIds, Number(addVehId)]);
    setAddVehId('');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setErr('Tytuł jest wymagany'); return; }
    if (endDate < startDate) { setErr('Data zakończenia nie może być wcześniejsza niż rozpoczęcia'); return; }
    setBusy(true); setErr(null);
    const payload: TripInput = {
      title: title.trim(), purpose, status,
      startDate, endDate,
      startTime: startTime || null, endTime: endTime || null,
      locationText: locationText.trim() || null,
      intakeId: intakeId === '' ? null : Number(intakeId),
      brigadeId: brigadeId === '' ? null : Number(brigadeId),
      notes: notes.trim() || null,
      assignments, vehicleIds,
    };
    try {
      if (isEdit) await api.update(trip!.id, payload);
      else await api.create(payload);
      onSaved();
    } catch (er) { setErr(er instanceof Error ? er.message : String(er)); setBusy(false); }
  };

  const onDelete = async () => {
    if (!trip || !confirm(`Usunąć wyjazd „${trip.title}"?`)) return;
    try { await api.remove(trip.id); onSaved(); }
    catch (er) { alert('Błąd: ' + (er instanceof Error ? er.message : String(er))); }
  };

  // Widok tylko do odczytu (brak uprawnień do planowania).
  if (readOnly) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
        <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl w-full max-w-[560px] max-h-[92vh] overflow-auto">
          <div className="px-5 py-3 border-b border-ink-100"><h2 className="text-[15px] font-semibold text-ink-900">{trip?.title}</h2></div>
          <div className="p-5 text-[13px] grid gap-2 text-ink-700">
            <div>{trip?.purposeName} · {trip?.statusName}</div>
            <div className="font-mono">{trip?.startDate}{trip?.endDate !== trip?.startDate ? ' – ' + trip?.endDate : ''}</div>
            {trip?.locationText && <div>📍 {trip.locationText}</div>}
            {trip?.brigadeName && <div>Brygada: {trip.brigadeName}</div>}
            {trip && trip.assignments.length > 0 && <div>Skład: {trip.assignments.map(a => `${a.employeeName} (${a.roleName})`).join(', ')}</div>}
            {trip && trip.vehicles.length > 0 && <div>Pojazdy: {trip.vehicles.map(v => v.label).join(', ')}</div>}
            {trip?.notes && <div className="text-ink-500 whitespace-pre-wrap">{trip.notes}</div>}
          </div>
          <div className="px-5 py-3 border-t border-ink-100 flex justify-end">
            <button onClick={onClose} className="btn btn-secondary">Zamknij</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ModalShell title={isEdit ? 'Edytuj wyjazd' : 'Nowy wyjazd'} onClose={onClose} onSubmit={submit} busy={busy} err={err} draftValue={{ title, purpose, status, startDate, endDate, startTime, endTime, locationText, intakeId, brigadeId, notes, assignments, vehicleIds, addEmpId, addRole, addVehId }}>
      <div className="grid grid-cols-2 gap-3">
        <label className="field"><span className="field-label">Cel *</span>
          <SearchableSelect value={purpose} onChange={v => setPurpose(v as TripPurpose)}>
            {(meta?.purposes ?? []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </SearchableSelect></label>
        <label className="field"><span className="field-label">Status</span>
          <SearchableSelect value={status} onChange={v => setStatus(v as TripStatus)}>
            {(meta?.statuses ?? []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </SearchableSelect></label>
      </div>
      <label className="field"><span className="field-label">Tytuł *</span>
        <input value={title} onChange={e => setTitle(e.target.value)} className="input"
               placeholder="np. Wiercenie studni — Kowalscy, Wólka"/></label>
      <div className="grid grid-cols-2 gap-3">
        <label className="field"><span className="field-label">Data od *</span>
          <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); if (endDate < e.target.value) setEndDate(e.target.value); }} className="input"/></label>
        <label className="field"><span className="field-label">Data do *</span>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="input"/></label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="field"><span className="field-label">Godz. od</span>
          <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="input"/></label>
        <label className="field"><span className="field-label">Godz. do</span>
          <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} className="input"/></label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="field"><span className="field-label">Lokalizacja (tekst)</span>
          <input value={locationText} onChange={e => setLocationText(e.target.value)} className="input" placeholder="miejscowość, adres"/></label>
        <label className="field"><span className="field-label">Brygada</span>
          <SearchableSelect value={brigadeId === '' ? '' : String(brigadeId)} onChange={v => setBrigadeId(v ? Number(v) : '')}>
            <option value="">— brak —</option>
            {brigadesList.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </SearchableSelect></label>
      </div>
      <label className="field"><span className="field-label">Ujęcie (opcjonalnie)</span>
        <SearchableSelect value={intakeId === '' ? '' : String(intakeId)} onChange={v => setIntakeId(v ? Number(v) : '')}>
          <option value="">— brak —</option>
          {intakesList.map(i => <option key={i.id} value={i.id}>{(i.number ? i.number + ' · ' : '') + (i.name ?? ('#' + i.id))}</option>)}
        </SearchableSelect></label>

      {/* Skład brygady */}
      <div className="border border-ink-100 rounded-md p-3">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-500 mb-2">Skład ({assignments.length})</div>
        {assignments.length > 0 && (
          <div className="flex flex-col gap-1 mb-2">
            {assignments.map((a, idx) => (
              <div key={a.employeeId} className="flex items-center gap-2 text-[13px]">
                <span className="flex-1 truncate">{empName(a.employeeId)}</span>
                <SearchableSelect value={a.role} onChange={v => setAssignments(assignments.map((x, i) => i === idx ? { ...x, role: v as TripRole } : x))} className="w-36">
                  {(meta?.roles ?? []).map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </SearchableSelect>
                <button type="button" onClick={() => setAssignments(assignments.filter((_, i) => i !== idx))} className="btn btn-ghost btn-icon text-ink-400 hover:text-red-500"><Icon name="x" className="w-3.5 h-3.5"/></button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <SearchableSelect value={addEmpId === '' ? '' : String(addEmpId)} onChange={v => setAddEmpId(v ? Number(v) : '')} className="flex-1">
            <option value="">— wybierz pracownika —</option>
            {people.filter(p => !assignments.some(a => a.employeeId === p.id)).map(p => <option key={p.id} value={p.id}>{p.fullName}</option>)}
          </SearchableSelect>
          <SearchableSelect value={addRole} onChange={v => setAddRole(v as TripRole)} className="w-36">
            {(meta?.roles ?? []).map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </SearchableSelect>
          <button type="button" onClick={addAssignment} className="btn btn-secondary btn-sm"><Icon name="plus" className="w-3.5 h-3.5"/></button>
        </div>
      </div>

      {/* Pojazdy */}
      <div className="border border-ink-100 rounded-md p-3">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-ink-500 mb-2">Pojazdy ({vehicleIds.length})</div>
        {vehicleIds.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {vehicleIds.map(vid => (
              <span key={vid} className="pill flex items-center gap-1" style={{ background: '#eef2ff' }}>
                {vehLabel(vid)}
                <button type="button" onClick={() => setVehicleIds(vehicleIds.filter(x => x !== vid))} className="text-ink-400 hover:text-red-500"><Icon name="x" className="w-3 h-3"/></button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <SearchableSelect value={addVehId === '' ? '' : String(addVehId)} onChange={v => setAddVehId(v ? Number(v) : '')} className="flex-1">
            <option value="">— wybierz pojazd/sprzęt —</option>
            {vehiclesList.filter(v => !vehicleIds.includes(v.id)).map(v => <option key={v.id} value={v.id}>{v.label}{v.registrationNumber ? ` (${v.registrationNumber})` : ''}</option>)}
          </SearchableSelect>
          <button type="button" onClick={addVehicle} className="btn btn-secondary btn-sm"><Icon name="plus" className="w-3.5 h-3.5"/></button>
        </div>
      </div>

      <label className="field"><span className="field-label">Notatki</span>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} className="textarea" rows={2}/></label>

      {isEdit && (
        <button type="button" onClick={onDelete} className="text-[12px] text-red-500 hover:text-red-700 text-left">Usuń wyjazd</button>
      )}
    </ModalShell>
  );
}

/** Zarządzanie brygadami (nazwa + kolor). */
function BrigadesModal({ onClose }: { onClose: () => void }) {
  const [list, setList] = useState<Brigade[]>([]);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3b82f6');
  const [err, setErr] = useState<string | null>(null);

  const reload = () => brigadesApi.list(false).then(setList).catch(e => setErr(String(e)));
  useEffect(() => { reload(); }, []);

  const add = async () => {
    if (!name.trim()) return;
    try { await brigadesApi.create({ name: name.trim(), color }); setName(''); resetDirty(); reload(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };
  const remove = async (b: Brigade) => {
    if (!confirm(`Usunąć brygadę „${b.name}"?`)) return;
    try { await brigadesApi.remove(b.id); reload(); }
    catch (e) { alert('Błąd: ' + (e instanceof Error ? e.message : String(e))); }
  };

  const { requestClose, confirmation, dialogProps, resetDirty } = useUnsavedChanges({
    value: { name, color }, onClose,
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl w-full max-w-[480px] max-h-[92vh] overflow-auto">
        <div className="px-5 py-3 border-b border-ink-100"><h2 className="text-[15px] font-semibold text-ink-900">Brygady</h2></div>
        <div className="p-5 grid gap-3">
          {err && <div className="text-red-600 text-[12px]">{err}</div>}
          {list.length > 0 && (
            <div className="flex flex-col gap-1">
              {list.map(b => (
                <div key={b.id} className="flex items-center gap-2 text-[13px]">
                  <span className="inline-block w-3.5 h-3.5 rounded-full" style={{ background: b.color }}/>
                  <span className="flex-1">{b.name}</span>
                  <button onClick={() => remove(b)} className="btn btn-ghost btn-icon text-ink-400 hover:text-red-500"><Icon name="x" className="w-3.5 h-3.5"/></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2 border-t border-ink-100 pt-3">
            <label className="field flex-1"><span className="field-label">Nowa brygada</span>
              <input value={name} onChange={e => setName(e.target.value)} className="input" placeholder="np. Brygada 1"/></label>
            <input type="color" value={color} onChange={e => setColor(e.target.value)} className="w-10 h-10 rounded border border-ink-200 cursor-pointer" title="Kolor"/>
            <button onClick={add} className="btn btn-primary"><Icon name="plus" className="w-3.5 h-3.5"/> Dodaj</button>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-ink-100 flex justify-end">
          <button onClick={requestClose} className="btn btn-secondary">Zamknij</button>
        </div>
      </div>
    </div>
  );
}
